<template>
  <div>
    <table
      v-if="tournaments.length"
      style="width: 100%;text-align: left"
      class="table mb-1"
    >
      <thead>
        <tr>
          <th style="width: 50%">{{ type ? type : "Tournament" }}</th>
          <th style="width: 10%">Type</th>
          <th style="width: 20%">Start Time</th>
          <th style="width: 10%; text-align: center">Players</th>
          <th style="width: 10%">Status</th>
        </tr>
      </thead>
      <tbody v-for="(tournament, i) in shownTournaments" :key="i">
        <tr :class="i % 2 === 0 ? 'even' : 'odd'" :style="rowStyle(tournament)">
          <td>
            <router-link
              :to="`/tournaments/${tournament.id || tournament.tournamentId}`"
              class="has-text-info"
            >
              {{ tournament.name }}
              <span
                v-if="tournament.recurrence && tournament.recurrence.repeat"
              >
                {{
                  tournament.startTime
                    | asDayjsFormat(
                      `DD/MM/YYYY${
                        tournament.recurrence.repeat.basis === "minute"
                          ? " HH:mm"
                          : ""
                      }`
                    )
                }}
              </span>
              <i v-if="tournament.minRating || tournament.maxRating">
                | Rating [{{ tournament.minRating }}-{{ tournament.maxRating }}]
              </i>
            </router-link>
          </td>
          <td>
            <p>{{ tournament | roundText }}</p>
            <p>
              {{ tournament | timeText }}
              <i>{{ tournament | privacyText }}</i>
            </p>
          </td>
          <td>
            {{ tournament.startTime | asDate }}
            <br />
            {{ tournament.startTime | asDayjsFormat("hh:mm A (UTCZ)") }}
          </td>
          <td style="text-align: center">
            <p>
              <span>
                {{ tournament.playerCount ? tournament.playerCount : 0 }}
              </span>
              <span v-if="tournament.playerLimit">
                / {{ tournament.playerLimit }}
              </span>
            </p>
            <p v-if="isOpen(tournament)">
              <button
                v-if="!isMine(tournament)"
                class="button is-primary is-small"
                @click="handleJoin(tournament)"
              >
                Join
              </button>
              <button
                v-else-if="!tournament.currentRound"
                class="button is-danger is-small"
                @click="handleLeave(tournament)"
              >
                Leave
              </button>
            </p>
          </td>
          <td>
            {{ tournament | statusText }}
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="pageSize">
      <button
        v-if="shownTournaments.length < tournaments.length"
        class="button is-small is-ghost"
        @click="handleShowMore"
      >
        {{ shownTournaments.length }} / {{ tournaments.length }} {{ type }}.
        Show more ...
      </button>
      <button
        v-if="shownNumber > pageSize"
        class="button is-small is-ghost"
        @click="shownNumber = pageSize"
      >
        Show less
      </button>
    </div>
  </div>
</template>

<script>
import { addPlayer, removePlayer } from "vue-chess-shared/tournament/helper";
import { mapGetters } from "vuex";
import { firestore } from "@/services/firebase";

export default {
  name: "TournamentTable",
  components: {},

  props: ["tournaments", "myTournaments", "type", "pageSize"],

  data() {
    return {
      shownNumber: 5,
    };
  },

  computed: {
    ...mapGetters(["getPlayer"]),

    shownTournaments() {
      if (!this.tournaments) return [];
      if (this.pageSize > 0) return this.tournaments.slice(0, this.shownNumber);
      return this.tournaments;
    },
  },

  filters: {
    statusText(tournament) {
      if (tournament.currentRound > tournament.totalRounds) return "Ended";
      if (tournament.currentRound === 0) return "Coming";
      return "Live";
    },

    roundText(tournament) {
      const currentRound = Math.min(
        tournament.currentRound,
        tournament.totalRounds
      );
      return `${currentRound}/${tournament.totalRounds} rounds - ${
        tournament.type === "Robin" ? "Round Robin" : tournament.type
      }`;
    },

    privacyText(tournament) {
      switch (tournament.privacy) {
        case "Open":
        case "Password":
          return "open";
        case "Private":
          return "private";
        case "Invite":
          return "by invite only";
        case "Paid":
          return "by registration only";
        default:
          return "";
      }
    },

    timeText(tournament) {
      return `${tournament.baseTimeControl}+${tournament.increment}`;
    },
  },

  methods: {
    isMine(tournament) {
      if (!this.getPlayer) return false;
      // check playerUsername list property of firebase tournament document

      if (
        tournament.playerUsernames &&
        tournament.playerUsernames.includes(this.getPlayer.username)
      ) {
        return true;
      }

      if (isNaN(tournament.tournamentId)) return false;

      // using list of my tournaments get from api.chessquad
      if (!this.myTournaments || !this.myTournaments.length) return false;
      return this.myTournaments.find(
        (t) => t.tournamentId === tournament.tournamentId
      );
    },

    rowStyle(tournament) {
      return {
        "background-color":
          tournament.currentRound === 0
            ? "paleturquoise"
            : this.isMine(tournament)
            ? "palegoldenrod"
            : tournament.currentRound > tournament.totalRounds
            ? "white"
            : "palegreen",
      };
    },

    isOpen(tournament) {
      if (!["Open", "Password"].includes(tournament.privacy)) return false;
      if (tournament.type === "Elim" && tournament.currentRound) return false;
      if (tournament.type === "Robin" && tournament.currentRound) return false;
      if (tournament.currentRound >= tournament.totalRounds) return false;
      if (
        tournament.minRating &&
        (!this.getPlayer ||
          !this.getPlayer.stats ||
          !(this.getPlayer.stats.rating >= tournament.minRating))
      ) {
        return false;
      }
      if (
        tournament.maxRating &&
        (!this.getPlayer ||
          !this.getPlayer.stats ||
          !(this.getPlayer.stats.rating <= tournament.maxRating))
      ) {
        return false;
      }
      return true;
    },

    handleJoin(tournament) {
      if (!tournament) return;
      if (tournament.privacy === "Open") {
        this.$buefy.dialog.confirm({
          title: `${tournament.name}`,
          message: `Are you sure you want to join ${tournament.name}?`,
          confirmText: "Join",
          onConfirm: () => this.joinTournament(tournament),
        });
      } else if (tournament.privacy === "Password") {
        this.$buefy.dialog.prompt({
          title: `${tournament.name}`,
          message: `Please enter the secret code to join ${tournament.name}`,
          inputAttrs: {
            placeholder: "secret code",
          },
          trapFocus: true,
          confirmText: "Join",
          onConfirm: (value) => this.joinTournament(tournament, value),
        });
      }
    },

    async joinTournament(tournament, password = null) {
      if (
        tournament.privacy === "Password" &&
        tournament.password !== password
      ) {
        this.$buefy.toast.open({
          message: "The secret code you entered was incorrect",
          type: "is-danger",
        });
        return;
      }

      // Add Player to firebase
      await addPlayer(firestore, this.getPlayer, tournament);

      this.$buefy.toast.open(`You have successfully joined ${tournament.name}`);

      this.$router.push(
        `/tournaments/${tournament.id || tournament.tournamentId}`
      );
    },

    handleLeave(tournament) {
      if (!tournament) return;
      this.$buefy.dialog.confirm({
        title: `${tournament.name}`,
        message: `Are you sure you want to leave ${tournament.name}?`,
        confirmText: "Leave",
        onConfirm: () => removePlayer(firestore, this.getPlayer, tournament),
      });
    },

    handleShowMore() {
      if (!this.pageSize) return;
      if (this.shownNumber < this.tournaments.length) {
        this.shownNumber += this.pageSize;
      }
    },
  },
};
</script>

<style scoped>
th {
  background-color: darkslateblue;
  color: whitesmoke !important;
}

td {
  vertical-align: middle !important;
}

p {
  white-space: nowrap;
  overflow: hidden;
}
</style>
